import * as React from 'react';
import styled from 'styled-components';
import { Formik, Form, Field, useFormikContext } from 'formik';

import Routes from 'security-app/routing/Routes';
import useActivePerspective from 'components/perspectives/hooks/useActivePerspective';
import { useStore } from 'stores/connect';
import { Button } from 'components/bootstrap';
import { Switch, FormikInput } from 'components/common';
import { EventNotificationsActions, EventNotificationsStore } from 'stores/event-notifications/EventNotificationsStore';
import useDashboards from 'views/components/dashboard/hooks/useDashboards';
import useSavedSearches from 'views/hooks/useSavedSearches';
import { FormikSelect, FormikMarkdownEditor } from 'security-app/components/common/FormikComponents';
import { Modal } from 'security-app/components/common';
import { Row, Col } from 'security-app/components/common/FormBaseStyles';
import { useCreateEventProcedureStep, useUpdateEventProcedureStep } from 'security-app/hooks/useEventProceduresAPI';
import type {
  CreateStepPayloadType,
  EventProcedureStepAPIType,
  ParametersType,
} from 'security-app/hooks/api/eventProceduresAPI.types';
import EntityCreateShareFormGroup from 'components/permissions/EntityCreateShareFormGroup';

import SearchQueryForm from './SearchQueryForm';
import EventProceduresParametersForm from './EventProceduresParametersForm';

const StyledForm = styled(Form)`
  width: 100%;

  & .form-group {
    width: 100%;
  }
`;

const StyledShareSection = styled.div`
  width: 100%;
`;

const INIT_EVENT_PROCEDURE_STEP = {
  title: '',
  description: '',
  type: '',
  notification_id: '',
  dashboard_id: '',
  query: '',
  parameters: {},
  use_saved_search: false,
  saved_search: '',
};

type Props = {
  onClose: () => void;
  eventProcedureStep?: EventProcedureStepAPIType;
  onSave?: (step: EventProcedureStepAPIType) => void;
  readOnly?: boolean;
};

type EventProcedureStepFormValues = {
  title: string;
  description: string;
  type: string;
  notification_id: string;
  dashboard_id: string;
  query: string;
  parameters: ParametersType;
  use_saved_search: boolean;
  saved_search: string;
};

const stepTypeOptions = [
  { label: 'Search Query', value: 'perform_search' },
  { label: 'Notification', value: 'execute_notification' },
  // { label: 'Link', value: 'link' },
  { label: 'Go To Dashboard', value: 'go_to_dashboard' },
];

const fetchNotifications = () => {
  EventNotificationsActions.listAll();
};

function EventProcedureStepForm({
  onClose,
  eventProcedureStep = undefined,
  onSave = () => {},
  readOnly = false,
}: Props) {
  const [stepType, setStepType] = React.useState(eventProcedureStep?.action?.config?.type || '');
  const [showAction, setShowAction] = React.useState<boolean>(!!eventProcedureStep?.action?.config || false);
  const notifications = useStore(EventNotificationsStore);
  const { data: dashboards } = useDashboards({
    page: 1,
    pageSize: 0,
    query: '',
    sort: { attributeId: 'title', direction: 'asc' },
    scope: 'read',
  });
  const { data: savedSearches } = useSavedSearches({
    query: '',
    page: 1,
    pageSize: 0,
    sort: { direction: 'asc', attributeId: 'title' },
  });

  const { createEventProcedureStep, creatingEventProcedureStep } = useCreateEventProcedureStep();
  const { updateEventProcedureStep, updatingEventProcedureStep } = useUpdateEventProcedureStep();

  React.useEffect(() => {
    fetchNotifications();
  }, []);

  const { activePerspective } = useActivePerspective();
  const isSecurityPerspective = activePerspective.id === 'security';
  const eventProcedureStepsRoute = isSecurityPerspective
    ? Routes.SECURITY_EVENTS.EVENT_PROCEDURES.list('steps')
    : Routes.CORE_EVENTS.EVENT_PROCEDURES.LIST('steps');

  const getInitialValuesWithStep = () => {
    if (!eventProcedureStep) return INIT_EVENT_PROCEDURE_STEP;

    const { title, description, action } = eventProcedureStep;
    let auxConfig = {};

    if (!action) {
      return {
        ...INIT_EVENT_PROCEDURE_STEP,
        title: title,
        description: description,
      };
    }

    switch (action.config.type) {
      case 'execute_notification':
        auxConfig = {
          notification_id: action.config.notification_id,
        };
        break;
      case 'go_to_dashboard':
        auxConfig = {
          dashboard_id: action.config.dashboard_id,
          parameters: Object.keys(action.config?.parameters).length > 0 ? action.config.parameters : {},
        };
        break;
      case 'perform_search':
        auxConfig = {
          query: action.config.query,
          parameters: Object.keys(action.config?.parameters).length > 0 ? action.config.parameters : {},
          streams: action.config.streams,
          stream_categories: action.config.stream_categories,
          use_saved_search: action.config.use_saved_search,
          saved_search: action.config.saved_search,
        };
        break;
      // case 'link':
      //   auxConfig = {
      //     link: action.config.link,
      //   };
      //   break;
      default:
        break;
    }

    return {
      ...INIT_EVENT_PROCEDURE_STEP,
      title: title,
      description: description,
      type: action.config.type,
      ...auxConfig,
    };
  };

  const formatNotifications = (items) => {
    if (!items || items.length === 0) return [];

    const formattedNotifications = items.map((n) => ({ label: n.title, value: n.id }));

    return formattedNotifications;
  };

  const formatDashboards = (items) => {
    const formattedDashboards = items.list.map((d) => ({ label: d._value.title, value: d._value.id }));

    return formattedDashboards;
  };

  const formatSavedSearches = (savedSearches?.list ?? []).map(({ id, title }) => ({ value: id, label: title }));

  const onSubmit = (values: CreateStepPayloadType) => {
    let payloadObj: any = {
      title: values.title,
      description: values.description,
    };

    if (showAction) {
      switch (stepType) {
        case 'execute_notification':
          payloadObj = {
            ...payloadObj,
            action: {
              config: {
                type: stepType,
                notification_id: values.notification_id || '',
              },
            },
          };
          break;
        case 'go_to_dashboard':
          payloadObj = {
            ...payloadObj,
            action: {
              config: {
                type: stepType,
                dashboard_id: values.dashboard_id,
                parameters: values.parameters,
              },
            },
          };
          break;
        case 'perform_search':
          payloadObj = {
            ...payloadObj,
            action: {
              config: {
                type: stepType,
                query: values.query,
                use_saved_search: values.use_saved_search,
                saved_search: values.saved_search,
                parameters: values.parameters,
                streams: values.streams,
                stream_categories: values.stream_categories,
              },
            },
          };
          break;
        // case 'link':
        //   payloadObj = {
        //     ...payloadObj,
        //     action: {
        //       config: {
        //         type: values.type,
        //         link: values.link,
        //       }
        //     }
        //   };
        //   break;
        default:
          break;
      }
    }

    if (eventProcedureStep?.id) {
      payloadObj = {
        ...payloadObj,
        id: eventProcedureStep.id,
        _scope: eventProcedureStep._scope,
      };
    } else {
      payloadObj = {
        ...payloadObj,
        share_request: values.share_request,
      };
    }

    if (eventProcedureStep?.id) {
      updateEventProcedureStep({ stepId: eventProcedureStep.id, step: payloadObj });
    } else {
      createEventProcedureStep(payloadObj).then((response) => onSave(response));
    }
    onClose();
  };

  const onTypeChange = (value: string) => {
    setStepType(value);
  };

  const handleCancel = (resetForm: () => void) => () => {
    resetForm();
    onClose();
  };

  const preventSubmitOnEnter = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') return false;

    return true;
  };

  const renderFormType = (values) => {
    switch (stepType) {
      case 'execute_notification':
        return (
          <Field name="notification_id">
            {({ field }) => (
              <FormikSelect
                field={field}
                options={formatNotifications(notifications.all)}
                matchProp="value"
                clearable={false}
                onChange={field.onChange}
                label="Select Notification"
                disabled={readOnly}
                menuPlacement="top"
              />
            )}
          </Field>
        );
      case 'go_to_dashboard':
        return (
          <>
            <Field name="dashboard_id">
              {({ field }) => (
                <FormikSelect
                  field={field}
                  options={formatDashboards(dashboards)}
                  matchProp="value"
                  clearable={false}
                  onChange={field.onChange}
                  label="Select Dashboard"
                  disabled={readOnly}
                  menuPlacement="top"
                />
              )}
            </Field>
            <EventProceduresParametersForm viewType="dashboard" viewId={values?.dashboard_id} readOnly={readOnly} />
          </>
        );
      case 'perform_search':
        return <SearchQueryForm savedSearches={formatSavedSearches} readOnly={readOnly} />;
      // case 'link':
      //   return (
      //     <Col $fullWidth $width="100%" $gap="0.3rem" $align="stretch">
      //       <ControlLabel>Link</ControlLabel>
      //       <Field name="link" styled={{ width: '100%' }}>
      //         {({ field }) => (
      //           <Input id={field.name}
      //             name={field.name}
      //             placeholder="Link"
      //             type="text"
      //             value={field.value}
      //             onChange={field.onChange}
      //             onBlur={field.onBlur}
      //             bsStyle={(field.error && field.touched) ? 'error' : null} />
      //         )}
      //       </Field>
      //     </Col>
      //   );
      default:
        return null;
    }
  };

  const ParameterResetEffects = () => {
    const { values, setFieldValue } = useFormikContext<EventProcedureStepFormValues>();
    const prevDashboardIdRef = React.useRef<string>();
    const prevSavedSearchRef = React.useRef<string>();
    const hasMounted = React.useRef(false);

    React.useEffect(() => {
      if (!hasMounted.current) {
        hasMounted.current = true;
        prevDashboardIdRef.current = values.dashboard_id;
        prevSavedSearchRef.current = values.saved_search;

        return;
      }

      const prevDashboardId = prevDashboardIdRef.current;
      if (
        stepType === 'go_to_dashboard' &&
        values.dashboard_id &&
        values.dashboard_id !== prevDashboardId &&
        !readOnly
      ) {
        setFieldValue('parameters', {});
      }

      prevDashboardIdRef.current = values.dashboard_id;
    }, [values.dashboard_id, values.saved_search, setFieldValue]);

    React.useEffect(() => {
      const prevSavedSearch = prevSavedSearchRef.current;
      if (
        stepType === 'perform_search' &&
        values.use_saved_search &&
        values.saved_search &&
        values.saved_search !== prevSavedSearch &&
        !readOnly
      ) {
        setFieldValue('parameters', {});
      }

      prevSavedSearchRef.current = values.saved_search;
    }, [values.saved_search, values.use_saved_search, setFieldValue]);

    return null; // No visible UI
  };

  return (
    <Modal
      show
      onClose={onClose}
      maxWidth="700px"
      title={`${eventProcedureStep?.id ? 'Edit' : 'Create'} Event Procedure Step`}
      showClose={false}
      headerElement={
        <div style={{ display: 'flex', flexDirection: 'row' }}>
          <Switch
            aria-label={`${showAction ? 'Hide' : 'Show'} Action Section`}
            id="toggle_action_section"
            data-testid="show-action-toggle"
            checked={showAction}
            onChange={() => setShowAction(!showAction)}
            disabled={readOnly}
          />
          &nbsp;Action
        </div>
      }>
      <Formik<EventProcedureStepFormValues>
        onSubmit={onSubmit}
        initialValues={getInitialValuesWithStep() || INIT_EVENT_PROCEDURE_STEP}>
        {({ isSubmitting, isValid, dirty, values, submitForm, resetForm, setFieldValue }) => (
          <StyledForm id="event-procedure-step-form" onKeyDown={preventSubmitOnEnter}>
            <ParameterResetEffects />
            <Col $width="100%">
              <Col $fullWidth $width="100%" $gap="0.3rem" $align="stretch">
                <FormikInput
                  type="text"
                  name="title"
                  id="title"
                  label="Title"
                  validate={(value) => !value && 'Must have a title'}
                  required
                  disabled={readOnly}
                />
              </Col>
              <Col $fullWidth $width="100%" $gap="0.3rem" $align="stretch">
                <Field
                  id="event_procedure_step_description"
                  name="description"
                  component={FormikMarkdownEditor}
                  label="Description"
                  height={150}
                  disabled={readOnly}
                />
              </Col>
              {readOnly && (
                <Row $fullWidth $width="100%" $justify="flex-end">
                  <Button
                    bsSize="sm"
                    bsStyle="link"
                    href={eventProcedureStepsRoute}
                    target="_blank"
                    rel="noopener noreferrer">
                    Manage Event Procedure Steps
                  </Button>
                </Row>
              )}
              {showAction && (
                <>
                  <Field name="type" label="Action Type">
                    {({ field }) => (
                      <FormikSelect
                        field={field}
                        options={stepTypeOptions}
                        onChange={onTypeChange}
                        matchProp="value"
                        clearable={false}
                        label="Action Type"
                        disabled={readOnly}
                      />
                    )}
                  </Field>
                  {renderFormType(values)}
                </>
              )}
              {!eventProcedureStep?.id && (
                <StyledShareSection>
                  <EntityCreateShareFormGroup
                    description="Search for a User or Team to add as collaborator on the event procedure step."
                    entityType="event_procedure_step"
                    onSetEntityShare={(payload) => setFieldValue('share_request', payload)}
                  />
                </StyledShareSection>
              )}
              <Row $justify="flex-end" $width="100%">
                <Button type="reset" onClick={handleCancel(resetForm)}>
                  Cancel
                </Button>
                <Button
                  type="button"
                  bsStyle="success"
                  onClick={submitForm}
                  disabled={
                    readOnly ||
                    !isValid ||
                    !dirty ||
                    creatingEventProcedureStep ||
                    updatingEventProcedureStep ||
                    isSubmitting
                  }>
                  {eventProcedureStep?.id ? 'Save' : 'Create'} Event Procedure Step
                </Button>
              </Row>
            </Col>
          </StyledForm>
        )}
      </Formik>
    </Modal>
  );
}

export default EventProcedureStepForm;
